import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api, { platformAPI } from '../services/api';
import {
  connectWhotOnlineSocket,
  whotOnlineSocket,
} from '../services/whotOnlineSocket';
import { buildInviteLink } from '../utils/inviteLinks';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import { WhotWeeklyLeaderboard } from '../components/WhotWeeklyLeaderboard';
import '../styles/Whot.css';

const TURN_TIME_OPTIONS = [120, 10, 20, 30, 40, 50, 60];
const BOT_TURN_TIME_OPTIONS = [10, 20];
const CREATE_KIND_BOT = 'bot';
const MIN_WHOT_BOT_WAGER = 1;

const getWhotBotDifficultyForWager = (wager) => {
  const numericWager = Number(wager || 0);
  if (!Number.isFinite(numericWager) || numericWager <= 0) return 'normal';
  if (numericWager <= 50) return 'medium'; // mapped to easy in logic
  if (numericWager <= 150) return 'hard'; // mapped to medium in logic
  return 'very_hard'; // mapped to hard in logic
};

const normalizeBotTurnTimeSeconds = (value) => {
  const numeric = Number(value);
  return BOT_TURN_TIME_OPTIONS.includes(numeric) ? numeric : 10;
};
const CREATE_KIND_FRIENDLY = 'friendly';
const CREATE_KIND_REAL = 'real';
export default function WhotOnlineLobby() {
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [maxPlayers, setMaxPlayers] = useState(2);
  const [turnTimeSeconds, setTurnTimeSeconds] = useState(10);
  const [betWager, setBetWager] = useState(50);
  const [minimumWager, setMinimumWager] = useState(10);
  const [houseRules, setHouseRules] = useState({
    whotEnabled: true,
    generalMarket14: true,
    pick2: true,
    holdOn1: true,
    pick3On5: true,
    suspension8: true,
    checkUpWithActionCard: false,
    oneRound: true,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState(CREATE_KIND_FRIENDLY);
  const [rooms, setRooms] = useState([]);
  const [roomFilter, setRoomFilter] = useState('all');
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [roomSearch, setRoomSearch] = useState('');
  const [joinId, setJoinId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return getGlobalSoundEnabled();
  });

  const [friends, setFriends] = useState([]);
  const [invitedFriendId, setInvitedFriendId] = useState('');
  const [friendSearch, setFriendSearch] = useState('');
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  const localUserId = Number(user?.id || 0);

  useEffect(() => {
    setGlobalSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => subscribeToGlobalSoundPreference(setSoundEnabled), []);

  const isVisibleRoom = (room) => {
    if (!room?.invitedUserId) return true;
    const invitedId = Number(room.invitedUserId);
    return invitedId === localUserId || Number(room.creatorId) === localUserId;
  };

  const visibleRooms = useMemo(
    () => rooms.filter(isVisibleRoom),
    [rooms, localUserId]
  );

  const myCreatedRooms = useMemo(
    () => visibleRooms.filter((room) => Number(room.creatorId) === localUserId),
    [visibleRooms, localUserId]
  );

  const activeCreatedCount = useMemo(
    () => myCreatedRooms.filter((room) => String(room.gameStatus || '').toLowerCase() !== 'finished').length,
    [myCreatedRooms]
  );

  const availableRooms = useMemo(
    () => visibleRooms.filter((room) => Number(room.creatorId) !== localUserId),
    [visibleRooms, localUserId]
  );

  const filteredAvailableRooms = useMemo(() => {
    return availableRooms.filter((room) => {
      const isBetRoom = Number(room?.wager || 0) > 0 || room?.challengeId;
      if (roomFilter === 'friendly' && isBetRoom) return false;
      if (roomFilter === 'betting' && !isBetRoom) return false;

      const query = String(roomSearch || '').trim().toLowerCase();
      if (!query) return true;

      const roomId = String(room?.roomId || '').toLowerCase();
      const creatorName = String(room?.creatorName || '').toLowerCase();
      return roomId.includes(query) || creatorName.includes(query);
    });
  }, [availableRooms, roomFilter, roomSearch]);

  const roomByJoinCode = useMemo(
    () => visibleRooms.find((room) => String(room.roomId) === String(joinId || '').trim()),
    [visibleRooms, joinId]
  );

  const summarizeHouseRules = (rules = {}) => {
    const items = [];
    if (rules.whotEnabled) items.push('WHOT');
    if (rules.pick2) items.push('Pick 2');
    if (rules.pick3On5) items.push('Pick 3 on 5');
    if (rules.suspension8) items.push('Suspension');
    if (rules.holdOn1) items.push('Hold On');
    if (rules.generalMarket14) items.push('General Market');
    if (rules.checkUpWithActionCard) items.push('Action Check Up');
    if (rules.oneRound) items.push('One Round');
    return items.length ? items.join(', ') : 'Default rules';
  };

  const resetModalFields = (kind) => {
    setCreateKind(kind);
    if (kind === CREATE_KIND_BOT) {
      setMaxPlayers(2);
      setInvitedFriendId('');
      setFriendSearch('');
      setBetWager(0);
      setTurnTimeSeconds((prev) => normalizeBotTurnTimeSeconds(prev));
    }
  };

  const openCreateModal = (kind) => {
    setError('');
    resetModalFields(kind);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const ensureWhotSocketReady = async (timeoutMs = 8000) => {
    if (!token) return false;
    const sock = connectWhotOnlineSocket(token);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        resolve(Boolean(ok));
      };

      const timeoutId = window.setTimeout(() => finish(false), timeoutMs);

      const tryListRooms = () => {
        try {
          whotOnlineSocket.listRooms((resp) => {
            window.clearTimeout(timeoutId);
            finish(Boolean(resp?.ok));
          });
        } catch {
          window.clearTimeout(timeoutId);
          finish(false);
        }
      };

      // If already connected, emit immediately
      if (sock.connected) {
        tryListRooms();
      } else {
        // Wait for the connect event first (important in production where handshake takes longer)
        sock.once('connect', () => tryListRooms());
        sock.once('connect_error', () => {
          window.clearTimeout(timeoutId);
          finish(false);
        });
      }
    });
  };

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setBetWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
      } catch (err) {
        console.error('Failed to load platform settings:', err);
      }
    };

    loadPlatformSettings();
  }, []);

  useEffect(() => {
    if (!token) return;

    connectWhotOnlineSocket(token);
    whotOnlineSocket.onQueueMatched((payload) => {
      if (!payload?.roomId) return;
      setQueueing(false);
      navigate(`/whot-online/room/${payload.roomId}`);
    });

    whotOnlineSocket.onQueueError((payload) => {
      const message = payload?.message || 'Match failed';
      setQueueing(false);
      toast.error(message);
    });

    whotOnlineSocket.onRoomsUpdate((payload) => {
      setRooms(payload?.rooms || []);
    });

    whotOnlineSocket.listRooms((resp) => {
      if (resp?.ok) setRooms(resp.rooms || []);
    });

    return () => {
      whotOnlineSocket.removeListeners();
    };
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const loadFriends = async () => {
      try {
        const res = await api.get('/friends');
        setFriends(res.data || []);
      } catch (err) {
        console.error('Failed to load friends:', err);
      }
    };

    loadFriends();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    let timerId;
    const loadOnlineStatus = async () => {
      try {
        const res = await api.get('/friends/online-status');
        const statusMap = {};
        (res.data || []).forEach((friend) => {
          statusMap[friend.friend_id] = Boolean(friend.is_online);
        });
        setFriendsOnlineStatus(statusMap);
      } catch (err) {
        console.error('Failed to load friend online status:', err);
      }
    };

    loadOnlineStatus();
    timerId = window.setInterval(loadOnlineStatus, 5000);
    return () => window.clearInterval(timerId);
  }, [token]);

  const copyText = async (text) => {
    if (!text) return false;

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fall through to manual copy fallback.
    }

    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.top = '-9999px';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);
      return copied;
    } catch {
      return false;
    }
  };

  const shareRoomLink = async (roomId, modeLabel = 'challenge') => {
    if (!roomId) return;

    const link = buildInviteLink(`/whot-online/room/${roomId}`);
    const copied = await copyText(link);

    if (copied) {
      toast.success(`WHOT ${modeLabel} link copied`);
      return;
    }

    window.prompt('Copy this WHOT invite link:', link);
  };

  const createFriendlyOrBotRoom = async (selectedMode, opts = {}) => {
    if (invitedFriendId && Number(maxPlayers) !== 2) {
      setError('Direct challenges support 2 players only.');
      return;
    }

    if (!token) {
      const message = 'You must be logged in to create a room.';
      setError(message);
      toast.error(message);
      return;
    }
    connectWhotOnlineSocket(token);

    const effectiveTurnTimeSeconds = selectedMode === 'bot'
      ? normalizeBotTurnTimeSeconds(turnTimeSeconds)
      : turnTimeSeconds;

    setError('');
    setLoading(true);

    whotOnlineSocket.createRoom(
      {
        mode: selectedMode,
        maxPlayers: selectedMode === 'bot' ? 2 : (Number(maxPlayers) || 2),
        houseRules,
        turnTimeSeconds: effectiveTurnTimeSeconds,
        invitedUserId: invitedFriendId ? Number(invitedFriendId) : null,
      },
      (resp) => {
        setLoading(false);
        if (!resp?.ok) {
          const message = resp?.error || 'Failed to create room';
          setError(message);
          toast.error(message);
          return;
        }

        const shouldShare = Boolean(opts.shareAfterCreate) && selectedMode !== 'bot';
        if (shouldShare) {
          void shareRoomLink(resp.roomId, 'friendly room');
        }
        closeCreateModal();
        navigate(`/whot-online/room/${resp.roomId}`);
      }
    );
  };

  const createRealChallenge = async (opts = {}) => {
    if (invitedFriendId && Number(maxPlayers) !== 2) {
      setError('Direct challenges support 2 players only.');
      return;
    }

    const wager = Number(betWager);
    if (!Number.isFinite(wager) || wager < minimumWager) {
      setError(`Wager must be at least ${minimumWager} wikipenny`);
      return;
    }

    const balance = Number(user?.balance || 0);
    if (balance < wager) {
      const message = `Insufficient balance. You need ${wager} wikipenny.`;
      setError(message);
      toast.error(message);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const socketReady = await ensureWhotSocketReady();
      if (!socketReady) {
        setLoading(false);
        const message = 'WHOT server is offline. Betting lobby creation blocked.';
        setError(message);
        toast.error(message);
        return;
      }

      const res = await api.post('/multiplayer/create', {
        maxPlayers: Number(maxPlayers) || 2,
        wager,
        game: 'whot',
        meta: {
          houseRules,
          turnTimeSeconds,
          invitedUserId: invitedFriendId ? Number(invitedFriendId) : null,
        },
      });

      const challengeId = res.data?.challenge?.id;
      if (!challengeId) throw new Error('Failed to create betting lobby');

      const resp = await new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          resolve(value || { ok: false, error: 'Failed to create betting room' });
        };

        const timeoutId = window.setTimeout(() => {
          finish({ ok: false, error: 'WHOT server did not acknowledge room creation in time.' });
        }, 8000);

        try {
          whotOnlineSocket.createRoom(
            {
              roomId: String(challengeId),
              challengeId: String(challengeId),
              wager,
              mode: 'pvp',
              maxPlayers: Number(maxPlayers) || 2,
              houseRules,
              turnTimeSeconds,
              invitedUserId: invitedFriendId ? Number(invitedFriendId) : null,
            },
            (ack) => {
              window.clearTimeout(timeoutId);
              finish(ack);
            }
          );
        } catch {
          window.clearTimeout(timeoutId);
          finish({ ok: false, error: 'Failed to contact WHOT server.' });
        }
      });

      setLoading(false);
      if (!resp?.ok) {
        setError(resp?.error || 'Failed to create betting room');
        try {
          await api.post(`/multiplayer/cancel/${challengeId}`);
          await refreshUser();
        } catch {
          // ignore cancel failure
        }
        return;
      }

      const shouldShare = Boolean(opts.shareAfterCreate);
      if (shouldShare) {
        await shareRoomLink(resp.roomId, 'bet room');
      }
      await refreshUser();
      closeCreateModal();
      navigate(`/whot-online/room/${resp.roomId}`);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to create betting lobby';
      setError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  const createBotChallenge = async () => {
    const numericWager = Number(betWager);
    const wager = Number.isFinite(numericWager) ? numericWager : 0;
    const effectiveTurnTimeSeconds = normalizeBotTurnTimeSeconds(turnTimeSeconds);

    if (wager <= 0) {
      await createFriendlyOrBotRoom('bot');
      return;
    }

    if (wager > 0 && wager < MIN_WHOT_BOT_WAGER) {
      setError(`Enter 0 for friendly play or at least ${MIN_WHOT_BOT_WAGER} wikipenny for a wagered bot match`);
      return;
    }

    const balance = Number(user?.balance || 0);
    if (balance < wager) {
      const message = `Insufficient balance. You need ${wager} wikipenny.`;
      setError(message);
      toast.error(message);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const socketReady = await ensureWhotSocketReady();
      if (!socketReady) {
        setLoading(false);
        const message = 'WHOT server is offline. Betting lobby creation blocked.';
        setError(message);
        toast.error(message);
        return;
      }

      const res = await api.post('/multiplayer/create', {
        maxPlayers: 2,
        wager,
        game: 'whot',
        meta: {
          botMatch: true,
          botCount: 1,
          houseRules,
          turnTimeSeconds,
        },
      });

      const challengeId = res.data?.challenge?.id;
      if (!challengeId) throw new Error('Failed to create wagered bot lobby');
      const botDifficulty = getWhotBotDifficultyForWager(wager);

      const resp = await new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          resolve(value || { ok: false, error: 'Failed to create wagered bot room' });
        };

        const timeoutId = window.setTimeout(() => {
          finish({ ok: false, error: 'WHOT server did not acknowledge bot room creation in time.' });
        }, 8000);

        try {
          whotOnlineSocket.createRoom(
            {
              roomId: String(challengeId),
              challengeId: String(challengeId),
              wager,
              mode: 'bot',
              maxPlayers: 2,
              houseRules,
              turnTimeSeconds: effectiveTurnTimeSeconds,
              botDifficulty,
            },
            (ack) => {
              window.clearTimeout(timeoutId);
              finish(ack);
            }
          );
        } catch {
          window.clearTimeout(timeoutId);
          finish({ ok: false, error: 'Failed to contact WHOT server.' });
        }
      });

      setLoading(false);
      if (!resp?.ok) {
        setError(resp?.error || 'Failed to create wagered bot room');
        try {
          await api.post(`/multiplayer/cancel/${challengeId}`);
          await refreshUser();
        } catch {
          // ignore cancel failure
        }
        return;
      }

      await refreshUser();
      closeCreateModal();
      navigate(`/whot-online/room/${resp.roomId}`);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to create wagered bot lobby';
      setError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  const handleCreateFromModal = async (opts = {}) => {
    if (createKind === CREATE_KIND_BOT) {
      await createBotChallenge();
      return;
    }

    if (createKind === CREATE_KIND_FRIENDLY) {
      await createFriendlyOrBotRoom('pvp', opts);
      return;
    }

    await createRealChallenge(opts);
  };

  const onJoinBetRoom = async (roomCode) => {
    const roomId = String(roomCode || '').trim();
    if (!roomId) return;

    const roomMatch = rooms.find((room) => String(room.roomId) === roomId && Number(room?.wager || 0) > 0);
    if (!roomMatch) {
      setError('Betting room not found');
      return;
    }

    const isAlreadyInRoom = (roomMatch.playerIds || []).some((id) => String(id) === String(user?.id));

    if (roomMatch.playerCount >= roomMatch.maxPlayers) {
      if (!isAlreadyInRoom) {
        setError('Betting room is full');
        return;
      }
    }

    const wager = Number(roomMatch?.wager || 0);
    const balance = Number(user?.balance || 0);
    if (wager > 0 && balance < wager) {
      const message = `Insufficient balance. You need ${wager} wikipenny.`;
      setError(message);
      toast.error(message);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const socketReady = await ensureWhotSocketReady();
      if (!socketReady) {
        setLoading(false);
        const message = 'WHOT server is offline. Betting join blocked.';
        setError(message);
        toast.error(message);
        return;
      }

      // Real-challenge rejoin should behave like friendly-room rejoin:
      // once you're already a participant, go straight back into the WHOT room.
      if (!isAlreadyInRoom) {
        await api.post(`/multiplayer/join/${roomId}`);
      }
      whotOnlineSocket.joinRoom({ roomId }, (resp) => {
        setLoading(false);
        if (!resp?.ok) {
          const message = resp?.error || 'Failed to join room';
          setError(message);
          toast.error(message);
          return;
        }

        refreshUser();
        navigate(`/whot-online/room/${roomId}`);
      });
    } catch (err) {
      setLoading(false);
      const message = err?.response?.data?.error || err?.message || 'Failed to join betting lobby';
      setError(message);
      toast.error(message);
    }
  };

  const onJoinRoom = async (roomCode) => {
    const roomId = String(roomCode || '').trim();
    if (!roomId) return;

    const room = visibleRooms.find((entry) => String(entry.roomId) === roomId);
    if (!room) {
      setError('Room not found');
      return;
    }

    const isBetRoom = Number(room?.wager || 0) > 0 || room?.challengeId;
    if (isBetRoom) {
      await onJoinBetRoom(roomId);
      return;
    }

    // For all rooms (betting or not): check if already in room or if room is full for NEW players
    if (room.playerCount >= room.maxPlayers) {
      // Allow rejoining if user is already in the room (normalize ID comparison)
      const isAlreadyInRoom = (room.playerIds || []).some(id => String(id) === String(user?.id));
      if (!isAlreadyInRoom) {
        setError('Room is full');
        return;
      }
    }

    setError('');
    setLoading(true);
    whotOnlineSocket.joinRoom({ roomId }, (resp) => {
      setLoading(false);
      if (!resp?.ok) {
        const message = resp?.error || 'Failed to join room';
        setError(message);
        toast.error(message);
        return;
      }
      navigate(`/whot-online/room/${roomId}`);
    });
  };

  const onQuickMatch = () => {
    setError('');
    setQueueing(true);
    whotOnlineSocket.queueJoin(
      {
        houseRules,
        turnTimeSeconds,
      },
      (resp) => {
        if (!resp?.ok) {
          const message = resp?.error || 'Failed to join queue';
          setQueueing(false);
          setError(message);
          toast.error(message);
        }
      }
    );
  };

  const onCancelMatch = () => {
    whotOnlineSocket.queueLeave({}, () => {
      setQueueing(false);
    });
  };

  const cancelRoom = async (room) => {
    const isBetRoom = Number(room?.wager || 0) > 0 || room?.challengeId;
    const wager = Number(room?.wager || 0);

    const closeSocketRoom = () => {
      whotOnlineSocket.cancelRoom({ roomId: room.roomId }, (resp) => {
        if (!resp?.ok) {
          toast.error(resp?.error || 'Failed to cancel room');
          return;
        }
        const message = isBetRoom ? 'Room canceled. Wagers refunded.' : 'Room canceled';
        toast.success(message);
      });
    };

    if (isBetRoom && wager > 0) {
      try {
        await api.post(`/multiplayer/cancel/${room.roomId}`);
        await refreshUser();
      } catch {
        // fallback to socket cancel
      }
    }
    closeSocketRoom();
  };

  const createTitle =
    createKind === CREATE_KIND_BOT
      ? 'Create Vs Bot Challenge'
      : createKind === CREATE_KIND_REAL
        ? 'Create Real Challenge'
        : 'Create Friendly Challenge';

  const isRealChallenge = createKind === CREATE_KIND_REAL;
  const isBotChallenge = createKind === CREATE_KIND_BOT;
  const shouldShowWagerField = isRealChallenge || isBotChallenge;
  const canInviteFriend = createKind !== CREATE_KIND_BOT;

  return (
    <div className="whot-lobby-container">
      {/* Animated Background */}
      <div className="whot-bg-animation">
        <div className="whot-card-glow"></div>
        <div className="whot-card-glow delay-1"></div>
        <div className="whot-card-glow delay-2"></div>
      </div>

      <div className="whot-lobby-content">
        {/* Header Section */}
        <div className="whot-lobby-header">
          <div className="header-left">
            <div className="whot-icon">🃏</div>
            <div>
              <h1 className="whot-lobby-title">WHOT Arena</h1>
              <p className="whot-lobby-subtitle">Real-time Multiplayer</p>
            </div>
          </div>
          <button
            className="sound-toggle"
            onClick={() => setSoundEnabled((prev) => !prev)}
            aria-label="Toggle sound"
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>

        {/* Stats Cards */}
        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">🎮</div>
            <div className="stat-info">
              <div className="stat-label">Active Players</div>
              <div className="stat-value">{rooms.length}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <div className="stat-label">Your Challenges</div>
              <div className="stat-value">{activeCreatedCount}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-info">
              <div className="stat-label">Quick Match</div>
              <div className="stat-value">{queueing ? 'Searching...' : 'Ready'}</div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Weekly Leaderboard and Quick Actions Grid */}
        <div className="whot-lobby-grid">
          <div className="grid-full">
            {/* Quick Actions Section */}
            <div className="quick-actions-section">
              <div className="section-header">
                <div className="section-header-left">
                  <h2 className="section-title">Quick Actions</h2>
                  <p className="section-subtitle">Start playing in seconds</p>
                </div>
                <button className="leaderboard-trigger" onClick={() => setShowLeaderboardModal(true)}>
                  🏆 Weekly Rankings
                </button>
              </div>

              <div className="quick-actions-grid">
                {!queueing ? (
                  <button className="action-card quick-match" onClick={onQuickMatch} disabled={loading}>
                    <div className="action-icon">⚡</div>
                    <div className="action-info">
                      <div className="action-title">Quick Match</div>
                      <div className="action-desc">Find random opponent</div>
                    </div>
                  </button>
                ) : (
                  <button className="action-card cancel-match" onClick={onCancelMatch}>
                    <div className="action-icon">❌</div>
                    <div className="action-info">
                      <div className="action-title">Cancel Search</div>
                      <div className="action-desc">Stop matchmaking</div>
                    </div>
                  </button>
                )}

                <div className="action-card join-code">
                  <div className="action-icon">🔑</div>
                  <div className="action-info">
                    <div className="action-title">Join by Code</div>
                    <input
                      type="text"
                      className="code-input"
                      value={joinId}
                      onChange={(e) => setJoinId(e.target.value)}
                      placeholder="Enter room ID"
                    />
                    {roomByJoinCode && (
                      <div className="code-hint">Found: {roomByJoinCode.creatorName}</div>
                    )}
                  </div>
                  <button
                    className="join-btn"
                    disabled={loading || !joinId}
                    onClick={() => onJoinRoom(joinId)}
                  >
                    Join
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Challenge Section */}
        <div className="create-section">
          <div className="section-header">
            <h2 className="section-title">Create Challenge</h2>
            <p className="section-subtitle">Choose your game mode</p>
          </div>

          <div className="create-cards">
            <button
              className="create-card"
              onClick={() => openCreateModal(CREATE_KIND_BOT)}
              disabled={loading || queueing}
            >
              <div className="create-icon">🤖</div>
              <div className="create-info">
                <div className="create-title">Vs Bot</div>
                <div className="create-desc">Practice against AI</div>
              </div>
              <div className="create-arrow">→</div>
            </button>

            <button
              className="create-card"
              onClick={() => openCreateModal(CREATE_KIND_FRIENDLY)}
              disabled={loading || queueing}
            >
              <div className="create-icon">👥</div>
              <div className="create-info">
                <div className="create-title">Friendly Challenge</div>
                <div className="create-desc">Play with friends</div>
              </div>
              <div className="create-arrow">→</div>
            </button>

            <button
              className="create-card real"
              onClick={() => openCreateModal(CREATE_KIND_REAL)}
              disabled={loading || queueing}
            >
              <div className="create-icon">💰</div>
              <div className="create-info">
                <div className="create-title">Real Challenge</div>
                <div className="create-desc">Win wikipenny</div>
              </div>
              <div className="create-arrow">→</div>
            </button>

            <button
              className="create-card"
              onClick={() => navigate('/whot/tournaments')}
              disabled={loading || queueing}
              style={{ background: 'linear-gradient(135deg, #e11d48, #be123c)' }}
            >
              <div className="create-icon">🏆</div>
              <div className="create-info">
                <div className="create-title">Arena Mode</div>
                <div className="create-desc">Whot Tournaments</div>
              </div>
              <div className="create-arrow">→</div>
            </button>
          </div>

        </div>

        {/* My Challenges Section */}
        {myCreatedRooms.length > 0 && (
          <div className="challenges-section">
            <div className="section-header">
              <h2 className="section-title">My Challenges</h2>
              <p className="section-subtitle">Your active rooms</p>
            </div>

            <div className="challenges-grid">
              {myCreatedRooms.map((room) => {
                const isBetRoom = Number(room?.wager || 0) > 0 || room?.challengeId;
                const isRoomFull = room.playerCount >= room.maxPlayers;
                const canCancelRoom = room.gameStatus !== 'in_progress' || !isRoomFull;

                return (
                  <div key={`mine-${room.roomId}`} className="challenge-card">
                    <div className="challenge-header">
                      <div className="challenge-id">Room #{room.roomId}</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <span className={`challenge-badge ${isBetRoom ? 'real' : 'friendly'}`}>
                          {isBetRoom ? '💰 REAL' : '🎮 FRIENDLY'}
                        </span>
                      </div>
                    </div>

                    <div className="challenge-stats">
                      <div className="stat">
                        <span className="stat-label">Players: </span>
                        <span className="stat-value">{room.playerCount}/{room.maxPlayers}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Status: </span>
                        <span className="stat-value">{room.gameStatus}</span>
                      </div>
                      {isBetRoom && (
                        <div className="stat">
                          <span className="stat-label">Wager</span>
                          <span className="stat-value highlight">{room.wager} wikipenny</span>
                        </div>
                      )}
                      {Number.isFinite(room.turnTimeoutMs) && (
                        <div className="stat">
                          <span className="stat-label">Turn Time:</span>
                          <span className="stat-value"> {room.turnTimeoutMs > 0 ? `${Math.round(room.turnTimeoutMs / 1000)}s` : 'No limit'}</span>
                        </div>
                      )}
                    </div>

                    {room.houseRules && (
                      <div className="challenge-rules">
                        <span className="rules-label">Rules:</span>
                        <span className="rules-value">{summarizeHouseRules(room.houseRules)}</span>
                      </div>
                    )}

                    <div className="match-invite-row">
                      <button
                        className="action-btn watch"
                        onClick={() => shareRoomLink(room.roomId, isBetRoom ? 'bet room' : 'friendly room')}
                        title="Copy invite link"
                        aria-label="Copy invite link"
                      >
                        📋
                      </button>
                    </div>

                    <div className="challenge-actions">
                      <button
                        className="action-btn watch"
                        onClick={() => navigate(`/whot-online/room/${room.roomId}?spectate=1`)}
                      >
                        👁️ Watch
                      </button>
                      <button
                        className="action-btn play"
                        onClick={() => navigate(`/whot-online/room/${room.roomId}`)}
                      >
                        🎮 Play
                      </button>
                      {canCancelRoom && (
                        <button
                          className="action-btn cancel"
                          onClick={() => cancelRoom(room)}
                        >
                          ❌ Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Challenges Section */}
        <div className="challenges-section">
          <div className="section-header">
            <h2 className="section-title">Available Challenges</h2>
            <p className="section-subtitle">Join games from other players</p>
          </div>

          <div className="filters-bar">
            <select
              className="filter-select"
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
            >
              <option value="all">All Games</option>
              <option value="friendly">Friendly Only</option>
              <option value="betting">Real Only</option>
            </select>
            <input
              type="text"
              className="search-input"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              placeholder="Search by room ID or host..."
            />
          </div>

          {filteredAvailableRooms.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🃏</div>
              <div className="empty-title">No challenges available</div>
              <div className="empty-desc">Create a challenge or wait for others to join</div>
            </div>
          ) : (
            <div className="challenges-grid">
              {filteredAvailableRooms.map((room) => {
                const isBetRoom = Number(room?.wager || 0) > 0 || room?.challengeId;
                const isRoomFull = room.playerCount >= room.maxPlayers;
                const isPlayer = user?.id && (room.playerIds || []).some((id) => String(id) === String(user.id));
                const canContinue = isPlayer && room.gameStatus !== 'finished';

                return (
                  <div key={`available-${room.roomId}`} className="challenge-card">
                    <div className="challenge-header">
                      <div className="challenge-id">Room #{room.roomId}</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <span className={`challenge-badge ${isBetRoom ? 'real' : 'friendly'}`}>
                          {isBetRoom ? '💰 REAL' : '🎮 FRIENDLY'}
                        </span>
                      </div>
                    </div>

                    <div className="challenge-host">
                      Host: <strong>{room.creatorName || 'Unknown'}</strong>
                    </div>

                    <div className="challenge-stats">
                      <div className="stat">
                        <span className="stat-label">Players</span>
                        <span className="stat-value">{room.playerCount}/{room.maxPlayers}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Status</span>
                        <span className="stat-value">{room.gameStatus}</span>
                      </div>
                      {isBetRoom && (
                        <div className="stat">
                          <span className="stat-label">Wager</span>
                          <span className="stat-value highlight">{room.wager} wikipenny</span>
                        </div>
                      )}
                    </div>

                    {room.houseRules && (
                      <div className="challenge-rules">
                        <span className="rules-label">Rules:</span>
                        <span className="rules-value">{summarizeHouseRules(room.houseRules)}</span>
                      </div>
                    )}

                    <div className="challenge-actions">
                      <button
                        className="action-btn watch"
                        onClick={() => navigate(`/whot-online/room/${room.roomId}?spectate=1`)}
                      >
                        👁️ Watch
                      </button>
                      <button
                        className={`action-btn play ${isRoomFull && !canContinue ? 'disabled' : ''}`}
                        disabled={isRoomFull && !canContinue}
                        onClick={() => {
                          if (!canContinue && isBetRoom) {
                            onJoinBetRoom(room.roomId);
                            return;
                          }
                          onJoinRoom(room.roomId);
                        }}
                      >
                        {canContinue ? '🎮 Continue' : (isRoomFull ? '🔒 Full' : '🎯 Join')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal-container whot-create-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-icon">🃏</div>
                <h3 className="modal-title">{createTitle}</h3>
                <button className="modal-close" onClick={closeCreateModal}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Players</label>
                  <select
                    className="form-select"
                    value={maxPlayers}
                    onChange={(e) => setMaxPlayers(Number(e.target.value))}
                    disabled={isBotChallenge}
                  >
                    <option value={2}>2 Players</option>
                    {!isBotChallenge && <option value={3}>3 Players</option>}
                    {!isBotChallenge && <option value={4}>4 Players</option>}
                  </select>
                  {isBotChallenge && (
                    <div className="form-hint">
                      WHOT bot matches are fixed to 2 players: you and 1 bot.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Turn Time</label>
                  <select
                    className="form-select"
                    value={turnTimeSeconds}
                    onChange={(e) => setTurnTimeSeconds(Number(e.target.value))}
                  >
                    {(isBotChallenge ? BOT_TURN_TIME_OPTIONS : TURN_TIME_OPTIONS).map((value) => (
                      <option key={value} value={value}>
                        {value === 120 ? '2 minutes' : `${value} seconds`}
                      </option>
                    ))}
                  </select>
                  {isBotChallenge && (
                    <div className="form-hint">
                      Bot matches allow only 10s or 20s per turn. No unlimited timer.
                    </div>
                  )}
                </div>

                {shouldShowWagerField && (
                  <div className="form-group form-group--full whot-wager-group">
                    <label className="form-label">Wager Amount</label>
                    <input
                      type="number"
                      className="form-select whot-wager-input"
                      min={isBotChallenge ? 0 : minimumWager}
                      step="1"
                      inputMode="numeric"
                      placeholder={isBotChallenge
                        ? `Enter amount (0 for friendly play)`
                        : `Enter amount (min ${minimumWager})`}
                      value={Number.isFinite(Number(betWager)) ? betWager : ''}
                      onChange={(e) => setBetWager(e.target.value)}
                    />
                    <div className="form-hint">
                      {isBotChallenge
                        ? `Bot wager limit based on balance (0 for friendly) • Balance: ${Number(user?.balance || 0)} wikipenny`
                        : `Minimum: ${minimumWager} wikipenny • Balance: ${Number(user?.balance || 0)} wikipenny`}
                    </div>
                  </div>
                )}

                {canInviteFriend && (
                  <div className="form-group form-group--full">
                    <label className="form-label">Invite Friend (Optional)</label>
                    <select
                      className="form-select invite-friend-select"
                      value={invitedFriendId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        setInvitedFriendId(nextId);
                        const match = friends.find((friend) => String(friend.friend_id) === String(nextId));
                        setFriendSearch(match ? String(match.friend_username || match.username || '') : '');
                      }}
                      disabled={Number(maxPlayers) !== 2}
                    >
                      <option value="">No friend selected</option>
                      {friends.map((friend) => {
                        const isOnline = Boolean(friendsOnlineStatus[friend.friend_id]);
                        const label = friend.friend_username || friend.username || 'Hidden ID Friend';
                        return (
                          <option key={friend.friendship_id || friend.friend_id} value={String(friend.friend_id)}>
                            {isOnline ? '🟢' : '⚪'} | {label}
                          </option>
                        );
                      })}
                    </select>
                    <div className="form-hint">
                      {Number(maxPlayers) !== 2 && "Direct invites only available for 2-player games"}
                    </div>
                  </div>
                )}

                <div className="form-group form-group--full">
                  <label className="form-label">House Rules</label>
                  <div className="rules-grid">
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.whotEnabled}
                        onChange={(e) => setHouseRules((r) => ({ ...r, whotEnabled: e.target.checked }))}
                      />
                      <span>WHOT cards enabled</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.pick2}
                        onChange={(e) => setHouseRules((r) => ({ ...r, pick2: e.target.checked }))}
                      />
                      <span>Pick 2</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.pick3On5}
                        onChange={(e) => setHouseRules((r) => ({ ...r, pick3On5: e.target.checked }))}
                      />
                      <span>Pick 3 on 5</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.suspension8}
                        onChange={(e) => setHouseRules((r) => ({ ...r, suspension8: e.target.checked }))}
                      />
                      <span>Suspension (8)</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.holdOn1}
                        onChange={(e) => setHouseRules((r) => ({ ...r, holdOn1: e.target.checked }))}
                      />
                      <span>Hold On (1)</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.generalMarket14}
                        onChange={(e) => setHouseRules((r) => ({ ...r, generalMarket14: e.target.checked }))}
                      />
                      <span>General Market (14)</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.checkUpWithActionCard}
                        onChange={(e) => setHouseRules((r) => ({ ...r, checkUpWithActionCard: e.target.checked }))}
                      />
                      <span>Allow check up with 8 / 1</span>
                    </label>
                    <label className="rule-checkbox">
                      <input
                        type="checkbox"
                        checked={houseRules.oneRound}
                        onChange={(e) => setHouseRules((r) => ({ ...r, oneRound: e.target.checked }))}
                      />
                      <span>One Round (No Reshuffle)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-create"
                  disabled={loading || queueing}
                  onClick={() => handleCreateFromModal()}
                >
                  {loading ? 'Creating...' : 'Create Challenge'}
                </button>
                {createKind !== CREATE_KIND_BOT && (
                  <button
                    className="join-btn"
                    disabled={loading || queueing}
                    onClick={() => handleCreateFromModal({ shareAfterCreate: true })}
                  >
                    Create & Copy Link
                  </button>
                )}
                <button className="btn-cancel" onClick={closeCreateModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Leaderboard Modal */}
        {showLeaderboardModal && (
          <div className="modal-overlay" onClick={() => setShowLeaderboardModal(false)}>
            <div className="modal-container leaderboard-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🏆 Weekly Whot Rankings</h3>
                <button className="modal-close" onClick={() => setShowLeaderboardModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <WhotWeeklyLeaderboard />
              </div>
              <div className="modal-footer">
                <button className="btn-create" onClick={() => setShowLeaderboardModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}








